import {
  MODULE_STAGES,
  isModuleStage,
  isWorkshopModule,
  type ModuleStage,
  type WorkshopModule,
  type WorkshopStageConfig,
} from './modules';

/**
 * Quanto tempo cada etapa costuma levar NESTA oficina (F3.7) — o que dá a
 * previsão por etapa sem ninguém digitar prazo em cada OS.
 *
 * Decisão do dono do produto (24/09): o gestor diz uma vez, nas
 * configurações, que "a cabine de pintura leva 1 dia"; cada OS calcula a
 * previsão da etapa sozinha, a partir da entrada nela. O prazo final da OS
 * continua sendo o que se combinou com o cliente, editável como sempre — a
 * duração da etapa não o reescreve.
 *
 * Nada configurado = nada muda: sem previsão, sem alerta. A sugestão abaixo só
 * preenche a tela de configuração; ela não vale até o gestor salvar, porque
 * uma previsão que a oficina não escolheu é uma promessa que ela não fez — e
 * o cliente a leria no app.
 *
 * Horas de relógio, não de expediente: a oficina não informa o expediente, e
 * "1 dia" que entra às 17h fica pronto às 17h do dia seguinte, que é como o
 * gestor pensa. A lista de escolhas é fechada pelo mesmo motivo do limiar de
 * parada: o valor mora num jsonb livre, e `0` ou `NaN` ali daria "toda OS
 * atrasada" ou "nenhuma".
 */
export const STAGE_DURATION_CHOICES = [2, 4, 8, 16, 24, 48, 72, 120, 168] as const;

export type StageDurationHours = (typeof STAGE_DURATION_CHOICES)[number];

/** Por módulo, por etapa, em horas. Etapa ausente = sem duração (sem previsão). */
export type StageDurations = Partial<Record<WorkshopModule, Partial<Record<string, number>>>>;

/**
 * O ponto de partida da tela de configuração. Funilaria: tempos de uma
 * oficina de porte médio, carro de reparo leve. Estética: um detalhamento
 * completo. Mecânica não tem etapas.
 */
export const SUGGESTED_STAGE_DURATIONS: Partial<Record<WorkshopModule, Partial<Record<ModuleStage, StageDurationHours>>>> = {
  funilaria: {
    desmontagem: 8,
    funilaria: 24,
    preparacao: 16,
    pintura: 8,
    polimento: 4,
    montagem: 8,
  },
  estetica: {
    lavagem: 2,
    descontaminacao: 2,
    correcao: 8,
    protecao: 4,
    inspecao: 2,
  },
};

export function isStageDurationChoice(value: unknown): value is StageDurationHours {
  return typeof value === 'number' && (STAGE_DURATION_CHOICES as readonly number[]).includes(value);
}

/** "8h", "1 dia", "3 dias", "1 semana". */
export function formatStageDuration(hours: number): string {
  if (hours === 168) return '1 semana';
  if (hours < 24 || hours % 24 !== 0) return `${hours}h`;
  const days = hours / 24;
  return `${days} ${days === 1 ? 'dia' : 'dias'}`;
}

/**
 * As durações válidas do `settings` da oficina. Tudo o que não for módulo
 * conhecido, etapa daquele módulo e duração da lista é descartado — em
 * silêncio, porque uma chave estranha no jsonb não pode derrubar o dashboard.
 *
 * "Etapa daquele módulo" é pela configuração da oficina (F3.8), quando vem:
 * a etapa criada por ela também tem tempo, e a escondida mantém o seu — a OS
 * que ficou nela continua com previsão.
 */
export function readStageDurations(raw: unknown, config?: WorkshopStageConfig): StageDurations {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: StageDurations = {};
  for (const [module, stages] of Object.entries(raw as Record<string, unknown>)) {
    if (!isWorkshopModule(module) || !stages || typeof stages !== 'object' || Array.isArray(stages)) continue;
    const clean: Record<string, number> = {};
    for (const [stage, hours] of Object.entries(stages as Record<string, unknown>)) {
      if (isModuleStage(module, stage, config) && isStageDurationChoice(hours)) clean[stage] = hours;
    }
    if (Object.keys(clean).length > 0) out[module] = clean;
  }
  return out;
}

/** A duração de uma etapa, ou `null` quando a oficina não configurou. */
export function stageDurationHours(
  durations: StageDurations,
  module: string | null | undefined,
  stage: string | null | undefined,
): number | null {
  if (!isWorkshopModule(module) || !stage) return null;
  const hours = durations[module]?.[stage];
  return isStageDurationChoice(hours) ? hours : null;
}

/** A oficina configurou alguma duração? Sem isso, nenhuma tela fala em previsão. */
export function hasStageDurations(durations: StageDurations): boolean {
  return Object.values(durations).some((stages) => stages && Object.keys(stages).length > 0);
}

/**
 * Status em que a etapa corre contra o relógio (F3.7). Só em execução:
 * esperando peça ou com o terceiro, o atraso não é da etapa — e a espera por
 * peça tem aviso próprio ("aguardando peça há X").
 */
export const STAGE_CLOCK_STATUSES = ['in_execution'] as const;

export interface StageForecast {
  stage: string;
  /** Entrada na etapa atual, ISO. */
  enteredAt: string;
  /** Entrada + duração configurada, ISO. */
  expectedBy: string;
  hours: number;
  /** Passou da previsão, com a OS em execução. */
  overdue: boolean;
}

/**
 * A previsão da etapa atual, ou `null` quando não há o que prever: OS sem
 * etapa, etapa sem duração configurada, ou status fora de
 * {@link STAGE_CLOCK_STATUSES} e de `waiting_part`/`outsourced` (que ainda
 * mostram a previsão, mas nunca "atrasada").
 */
export function stageForecast(params: {
  durations: StageDurations;
  module: string | null | undefined;
  stage: string | null | undefined;
  status: string;
  enteredAt: Date | string | null | undefined;
  now?: Date;
}): StageForecast | null {
  const { durations, module, stage, status, enteredAt } = params;
  if (!['in_execution', 'waiting_part', 'outsourced'].includes(status)) return null;
  const hours = stageDurationHours(durations, module, stage);
  if (hours === null || !stage || !enteredAt) return null;
  const entered = new Date(enteredAt);
  if (Number.isNaN(entered.getTime())) return null;
  const expected = new Date(entered.getTime() + hours * 3_600_000);
  const now = params.now ?? new Date();
  return {
    stage,
    enteredAt: entered.toISOString(),
    expectedBy: expected.toISOString(),
    hours,
    overdue: (STAGE_CLOCK_STATUSES as readonly string[]).includes(status) && expected.getTime() < now.getTime(),
  };
}

/** Módulos com etapas — os únicos que aparecem na configuração de durações. */
export const STAGED_MODULES = (Object.keys(MODULE_STAGES) as WorkshopModule[]).filter(
  (m) => MODULE_STAGES[m].length > 0,
);

/**
 * Módulos em que esperar peça NÃO é "OS parada" (F3.7). Na funilaria, peça de
 * lataria encomendada leva dias ou semanas, e a OS esperando por ela acendia
 * "parada" todo dia até a peça chegar — o gestor não tem o que fazer além de
 * esperar. Ela sai do alerta de parada e aparece como informação: "aguardando
 * peça há 6 dias", que é o que ele precisa para cobrar o fornecedor.
 *
 * Mecânica fica de fora de propósito: peça de mecânica chega no dia, e dois
 * dias esperando é mesmo problema.
 */
export const WAITING_PART_NOT_STALE_MODULES = ['funilaria'] as const satisfies readonly WorkshopModule[];
